import { useEffect, useRef, useState } from "react";
import { useTenant } from "@/lib/tenant";
import { toast } from "sonner";
import {
  Upload, Trash2, Image as ImageIcon, Loader2, ExternalLink,
  Plus, Link as LinkIcon, Instagram, Music2, Youtube, Facebook,
} from "lucide-react";

const PLATFORM_META = {
  instagram: { label: "Instagram", icon: Instagram, color: "text-pink-400" },
  tiktok:    { label: "TikTok",    icon: Music2,    color: "text-cyan-300" },
  youtube:   { label: "YouTube",   icon: Youtube,   color: "text-red-400" },
  facebook:  { label: "Facebook",  icon: Facebook,  color: "text-blue-400" },
};

function PhotoLibrary() {
  const { api } = useTenant();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/media");
      setItems(r.data || []);
    } catch { /* swallow */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("image", f);
        fd.append("caption", "");
        await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch { fail++; }
    }
    setBusy(false);
    if (ok) toast.success(`Uploaded ${ok} image${ok > 1 ? "s" : ""}`);
    if (fail) toast.error(`${fail} upload${fail > 1 ? "s" : ""} failed`);
    await load();
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this image from the homepage gallery?")) return;
    try {
      await api.delete(`/media/${id}`);
      setItems(items.filter(i => i.id !== id));
      toast.success("Removed");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <label className="block border-2 border-dashed border-white/15 rounded-xl p-8 text-center hover:border-amber-400/60 transition-colors cursor-pointer bg-white/[0.02] mb-6"
             data-testid="media-dropzone">
        <input ref={inputRef} type="file" accept="image/*" multiple
               onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
               className="hidden" data-testid="media-input" />
        {busy ? (
          <div className="flex items-center justify-center gap-2 text-slate-300">
            <Loader2 className="animate-spin" size={18} /> Uploading…
          </div>
        ) : (
          <>
            <Upload size={26} className="mx-auto text-slate-400 mb-2" />
            <div className="text-sm font-semibold">Tap to upload photos</div>
            <div className="text-[11px] text-slate-400 mt-1">JPG · PNG · WEBP — max 12 MB each, multi-select supported</div>
          </>
        )}
      </label>

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs uppercase tracking-widest text-slate-400">
          {loading ? "Loading…" : `${items.length} image${items.length === 1 ? "" : "s"} live`}
        </h3>
      </div>

      {!loading && items.length === 0 && (
        <div className="text-center py-10 bg-white/[0.02] border border-white/5 rounded-lg text-slate-500 text-xs">
          No images yet — uploads appear here and on the homepage gallery.
        </div>
      )}

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
        {items.map(it => (
          <div key={it.id} data-testid={`media-item-${it.id}`}
               className="relative group aspect-square rounded-md overflow-hidden border border-white/5 bg-black/30">
            <img src={it.url} alt={it.caption || "Gallery"} loading="lazy"
                 className="w-full h-full object-cover" />
            <button onClick={() => remove(it.id)} title="Delete"
                    data-testid={`media-delete-${it.id}`}
                    className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SocialPosts() {
  const { api } = useTenant();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/social");
      setPosts(r.data || []);
    } catch { /* swallow */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setAdding(true);
    try {
      await api.post("/social", { url: url.trim(), caption: caption.trim() });
      setUrl(""); setCaption("");
      await load();
      toast.success("Added to homepage feed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add that URL");
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this post from the homepage feed?")) return;
    try {
      await api.delete(`/social/${id}`);
      setPosts(posts.filter(p => p.id !== id));
      toast.success("Removed");
    } catch { toast.error("Couldn't remove"); }
  };

  return (
    <div>
      <form onSubmit={add} className="bg-white/[0.03] border border-white/10 rounded-xl p-5 mb-6">
        <div className="grid md:grid-cols-[1fr_auto] gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5 block">Post URL</label>
            <div className="relative">
              <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={url} onChange={e => setUrl(e.target.value)}
                     placeholder="https://www.instagram.com/p/… or https://www.tiktok.com/@wilsoncontractors/video/…"
                     data-testid="social-url"
                     className="w-full bg-white/5 border border-white/10 rounded-md pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-amber-400/60 transition-colors" />
            </div>
            <input value={caption} onChange={e => setCaption(e.target.value)}
                   placeholder="Optional caption…"
                   data-testid="social-caption"
                   className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm mt-2 focus:outline-none focus:border-amber-400/60 transition-colors" />
          </div>
          <button type="submit" disabled={adding || !url.trim()}
                  data-testid="social-add"
                  className="bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-[#1a1a0f] font-semibold rounded-md px-5 py-2.5 text-sm flex items-center justify-center gap-2 self-end transition-colors">
            {adding ? <><Loader2 size={14} className="animate-spin" /> Adding…</> : <><Plus size={14} /> Add</>}
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Supports public <span className="text-pink-300">Instagram</span> posts/reels, <span className="text-cyan-300">TikTok</span> videos, <span className="text-red-300">YouTube</span> videos, and <span className="text-blue-300">Facebook</span> posts/videos. Each one shows as a live embed on the homepage.
        </p>
      </form>

      <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-3">
        {loading ? "Loading…" : `${posts.length} post${posts.length === 1 ? "" : "s"} live`}
      </h3>

      {!loading && posts.length === 0 && (
        <div className="text-center py-10 bg-white/[0.02] border border-white/5 rounded-lg text-slate-500 text-xs">
          No social posts yet — paste a URL above to get started.
        </div>
      )}

      <ul className="space-y-2">
        {posts.map(p => {
          const meta = PLATFORM_META[p.kind] || { label: p.kind, icon: LinkIcon, color: "text-slate-300" };
          const Icon = meta.icon;
          return (
            <li key={p.id} data-testid={`social-item-${p.id}`}
                className="bg-white/[0.03] border border-white/5 rounded-lg p-3 flex items-center gap-3">
              <span className={`shrink-0 ${meta.color}`}><Icon size={16} /></span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest ${meta.color}`}>{meta.label}</span>
                  <a href={p.url} target="_blank" rel="noreferrer"
                     className="text-xs text-slate-300 hover:text-amber-300 truncate flex items-center gap-1">
                    {p.url} <ExternalLink size={10} className="shrink-0" />
                  </a>
                </div>
                {p.caption && <div className="text-[11px] text-slate-400 truncate mt-0.5">{p.caption}</div>}
              </div>
              <button onClick={() => remove(p.id)} title="Remove"
                      data-testid={`social-delete-${p.id}`}
                      className="text-slate-400 hover:text-red-300 p-1.5 rounded hover:bg-white/5 transition-colors">
                <Trash2 size={14} />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AutoFeeds() {
  const { api } = useTenant();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fbSyncing, setFbSyncing] = useState(false);
  const [fbUrl, setFbUrl] = useState("");
  const [fbToken, setFbToken] = useState("");
  const [igToken, setIgToken] = useState("");
  const [igUserId, setIgUserId] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/social/config");
      setCfg(r.data);
      setFbUrl(r.data.facebook_page_url || "");
      setIgUserId(r.data.instagram_user_id || "");
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.put("/social/config", { facebook_page_url: fbUrl, facebook_page_token: fbToken, instagram_token: igToken, instagram_user_id: igUserId });
      setIgToken(""); setFbToken("");
      toast.success("Saved — homepage feeds updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/social/instagram/sync");
      toast.success(`Synced ${r.data.count} Instagram post${r.data.count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed — check token / IG user id");
    } finally { setSyncing(false); }
  };

  const syncFb = async () => {
    setFbSyncing(true);
    try {
      const r = await api.post("/social/facebook/sync");
      toast.success(`Synced ${r.data.count} Facebook post${r.data.count === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed — check page token");
    } finally { setFbSyncing(false); }
  };

  if (loading) return <div className="text-slate-400 text-sm">Loading…</div>;

  return (
    <div className="space-y-6">
      {/* Facebook */}
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3"><Facebook size={16} className="text-blue-400" />
          <h3 className="font-semibold text-sm">Facebook Page</h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          Connect your Facebook Page to auto-show your latest photos and videos on the homepage. Updates hourly.
        </p>
        {cfg?.facebook_token_set ? (
          <div className="mb-4 flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2 text-xs">
            <div>
              <span className="text-emerald-300 font-semibold">Connected{cfg.facebook_page_name ? ` · ${cfg.facebook_page_name}` : ""} · Auto-syncs hourly</span>
              {cfg.facebook_last_sync && <span className="text-slate-400 ml-2">last sync {new Date(cfg.facebook_last_sync).toLocaleString()}</span>}
            </div>
            <button onClick={syncFb} disabled={fbSyncing} data-testid="fb-sync"
                    className="text-emerald-300 hover:text-emerald-200 underline">
              {fbSyncing ? "Syncing…" : "Sync now"}
            </button>
          </div>
        ) : (
          <details className="mb-4">
            <summary className="text-xs text-amber-300 cursor-pointer hover:text-amber-200">📘 How to get a Facebook Page token (10 min)</summary>
            <ol className="mt-2 space-y-1.5 text-[12px] text-slate-300 leading-relaxed list-decimal pl-5">
              <li>Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="text-amber-300 underline">Graph API Explorer</a>.</li>
              <li>Top-right: select your app (reuse the one from Instagram setup, or create one called &quot;Wilson Site&quot;).</li>
              <li>Click <strong>Get Token → Get User Access Token</strong>. Tick permissions: <code>pages_show_list</code>, <code>pages_read_engagement</code>.</li>
              <li>Click <strong>Generate</strong>, copy that token, then paste it into Meta&apos;s <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noreferrer" className="text-amber-300 underline">Access Token Debugger</a> and click <strong>Extend Access Token</strong> — this gives you a long-lived (~60 day) token.</li>
              <li>Back in Graph API Explorer, with that extended token active, run a <strong>GET</strong> request to <code>/me/accounts</code>.</li>
              <li>Find your Page in the results and copy <strong>its own</strong> <code>access_token</code> value (not the user token) — this Page token keeps working as long as you don&apos;t revoke the app.</li>
              <li>Paste that Page Access Token below ↓</li>
            </ol>
          </details>
        )}
        {cfg?.facebook_last_error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-md p-2.5 text-xs text-red-200 mb-3">
            <strong>Last error:</strong> {cfg.facebook_last_error}
          </div>
        )}
        <label className="block mb-3">
          <span className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5 block">Page access token</span>
          <input type="password" value={fbToken} onChange={(e) => setFbToken(e.target.value)}
                 placeholder={cfg?.facebook_token_set ? "(leave blank to keep current)" : "Paste your Page token here"}
                 data-testid="fb-token"
                 className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-amber-400/60 transition-colors" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5 block">Page URL <span className="text-slate-500">(used for the &quot;View page&quot; link)</span></span>
          <input value={fbUrl} onChange={(e) => setFbUrl(e.target.value)}
                 placeholder="https://www.facebook.com/wilsoncontractors.co.uk"
                 data-testid="fb-page-url"
                 className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400/60 transition-colors" />
        </label>
      </div>

      {/* Instagram */}
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3"><Instagram size={16} className="text-pink-400" />
          <h3 className="font-semibold text-sm">Instagram (Graph API)</h3>
        </div>
        {cfg?.instagram_token_set ? (
          <div className="mb-4 flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2 text-xs">
            <div>
              <span className="text-emerald-300 font-semibold">Connected · Auto-syncs hourly</span>
              {cfg.instagram_last_sync && <span className="text-slate-400 ml-2">last sync {new Date(cfg.instagram_last_sync).toLocaleString()}</span>}
              {cfg.instagram_token_expires_at && <span className="text-slate-500 ml-2">· token renews automatically (expires {new Date(cfg.instagram_token_expires_at).toLocaleDateString()})</span>}
            </div>
            <button onClick={sync} disabled={syncing} data-testid="ig-sync"
                    className="text-emerald-300 hover:text-emerald-200 underline">
              {syncing ? "Syncing…" : "Sync now"}
            </button>
          </div>
        ) : (
          <details className="mb-4">
            <summary className="text-xs text-amber-300 cursor-pointer hover:text-amber-200">📘 How to get an Instagram token (5 min)</summary>
            <ol className="mt-2 space-y-1.5 text-[12px] text-slate-300 leading-relaxed list-decimal pl-5">
              <li>Convert your IG to a <strong>Business or Creator account</strong> (Instagram app → Settings → Account).</li>
              <li>Link it to a Facebook Page (any page you admin).</li>
              <li>Go to <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="text-amber-300 underline">Graph API Explorer</a>.</li>
              <li>Top-right: select your app (create one called &quot;Wilson Site&quot; if needed), then <strong>Get Token → Get User Access Token</strong>.</li>
              <li>Tick permissions: <code>instagram_basic</code>, <code>pages_show_list</code>, <code>pages_read_engagement</code>.</li>
              <li>Click <strong>Generate</strong>, copy the token. Paste below ↓</li>
              <li><strong>IG User ID</strong> — paste your Instagram numeric user id (or leave as <code>me</code> if the token is IG-specific). Find at <a href="https://developers.facebook.com/tools/explorer/?method=GET&path=me%2Faccounts&version=v18.0" target="_blank" rel="noreferrer" className="text-amber-300 underline">/me/accounts → instagram_business_account.id</a>.</li>
            </ol>
          </details>
        )}
        {cfg?.instagram_last_error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-md p-2.5 text-xs text-red-200 mb-3">
            <strong>Last error:</strong> {cfg.instagram_last_error}
          </div>
        )}
        <label className="block mb-3">
          <span className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5 block">Access token</span>
          <input type="password" value={igToken} onChange={(e) => setIgToken(e.target.value)}
                 placeholder={cfg?.instagram_token_set ? "(leave blank to keep current)" : "Paste your token here"}
                 data-testid="ig-token"
                 className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-amber-400/60 transition-colors" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5 block">Instagram user id <span className="text-slate-500">(optional — defaults to &quot;me&quot;)</span></span>
          <input value={igUserId} onChange={(e) => setIgUserId(e.target.value)}
                 placeholder="17841401234567890 or 'me'"
                 data-testid="ig-user-id"
                 className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-amber-400/60 transition-colors" />
        </label>
      </div>

      <button onClick={save} disabled={busy} data-testid="autofeeds-save"
              className="bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-[#1a1a0f] font-semibold rounded-md px-6 py-2.5 text-sm flex items-center gap-2 transition-colors">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Save settings"}
      </button>
    </div>
  );
}

export default function Media() {
  const [tab, setTab] = useState("photos");
  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest mb-1">
            <ImageIcon size={13} /> Public site
          </div>
          <h1 className="text-2xl font-bold">Media &amp; Social</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Upload photos, paste social URLs, or set up auto-feeds from Facebook + Instagram.
            Everything here goes live on the public homepage straight away.
          </p>
        </div>
        <a href="/" target="_blank" rel="noreferrer"
           className="text-xs text-slate-300 hover:text-amber-300 flex items-center gap-1.5 bg-white/5 border border-white/10 px-3 py-2 rounded-md">
          View public site <ExternalLink size={12} />
        </a>
      </div>

      <div className="flex gap-1 border-b border-white/10 mb-6">
        {[
          { id: "photos", label: "Photo Gallery" },
          { id: "social", label: "Manual Posts" },
          { id: "autofeeds", label: "Auto-feeds (FB + IG)" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === t.id ? "border-amber-400 text-white" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "photos" && <PhotoLibrary />}
      {tab === "social" && <SocialPosts />}
      {tab === "autofeeds" && <AutoFeeds />}
    </div>
  );
}
